import { useEffect, useState } from 'react';
import { ToastContainer } from 'react-toastify';

import Layout from './components/Layout';
import Router from './components/Router';
import BrowserRouter from './components/Router/BrowserRouter';
import history from './components/Router/history';

import {
	getNotificationSettingsFromLocalStorage,
	getTokenFromLocalStorage,
	getUserFromLocalStorage,
	getViewedFeatureAnnouncementsFromLocalStorage,
	setNotificationSettingsInLocalStorage,
	setTokenInLocalStorage,
	setUserInLocalStorage,
} from './lib/local-storage';

import { releases } from './lib/releases';

import {
	getUserAndTokenFromSearchParams,
} from './lib/url';

import {
	SocketEventHandlers,
	SocketRPC,
	openSocketConnection,
	socket,
	socketEmit,
} from './lib/socket';

import FeatureAnnouncement from './components/FeatureAnnouncement';
import { pollChatrooms } from './lib/chat';
import { logout } from './lib/user';
import useChatrooms from './store/chatrooms';
import useNotifications from './store/notifications';
import useProducerLocations from './store/producer-locations';
import useUser from './store/user';

function App() {
	const { user, setUser, loading, setLoading } = useUser();
	const { setProducerLocations, setProducerLocationsLoaded } = useProducerLocations();
	const { setChatrooms } = useChatrooms.getState();
	const { setNotifications } = useNotifications.getState();
	const {settings} = useNotifications();
	const [newReleases, setNewReleases] = useState<any[]>([]);

	useEffect(() => {
		if (!user) return;

		const viewedFeatureAnnouncements = getViewedFeatureAnnouncementsFromLocalStorage() || [];
		const _newReleases = releases.filter((r) => !viewedFeatureAnnouncements.includes(r.version));

		if (_newReleases.length) {
			setNewReleases(_newReleases); // maybe some date filter as well?
		}

		const notificationSettingsInLocalStorage = getNotificationSettingsFromLocalStorage();
		if (!notificationSettingsInLocalStorage) {
			setNotificationSettingsInLocalStorage(settings);
		}

		let { producerKey, partnerKey } = user;
		if (!producerKey) {
			if (partnerKey !== 'wonder') {
				return;
			}
			producerKey = 'wonder';
		}

		setProducerLocationsLoaded(false);
		Promise.resolve()
			.then(() => {
				return socketEmit(SocketRPC.GetProducerLocations, { producerKey });
			})
			.then((res) => {
				if (!res.success) {
					throw new Error(res.error);
				}
				setProducerLocations(res.producerLocations);
				setProducerLocationsLoaded(true);
			});
	}, [user]);

	useEffect(() => {
		// Bind socket event handlers
		Object.entries(SocketEventHandlers).forEach(([event, fn]) => {
			socket.on(event, fn);
		});

		console.log('Loading Relay Super Admin Site...');
		const parsed = getUserAndTokenFromSearchParams(window.location.search);
		if (parsed) {
			const { user, token } = parsed;
			setUserInLocalStorage(user);
			setTokenInLocalStorage(token);
		}

		const user = getUserFromLocalStorage();

		if (!user) {
			console.log('No login was persisted');
			logout();
			return;
		}

		if (user) {
			setUser(user);
		}

		const token = getTokenFromLocalStorage();

		if (token) {
			openSocketConnection(token);
		} else {
			console.log('No token was persisted');
			logout();
			return;
		}

		setLoading(false);

		return () => {
			Object.keys(SocketEventHandlers).forEach((event) => {
				socket.off(event);
			});
		};
	}, []);

	useEffect(() => {
		const controller: AbortController | Record<string, never> = 'AbortController' in window ? new AbortController() : {};
		const signal = controller.signal;
		pollChatrooms({ signal, setChatrooms, setNotifications, chatNotificationsEnabled: settings?.newMessages?.enabled });
		return () => controller.abort();
	}, [JSON.stringify(settings.newMessages)]);

	useEffect(() => {
		if (!("Notification" in window)) {
			// alert("This browser does not support desktop notification");
			console.error('This browser does not support desktop notification');
			return;
	 	}
		
		const notificationPermission = Notification.permission;
		console.log('notificationPermission', notificationPermission);

		if (notificationPermission === 'default') {
			Notification.requestPermission().then((res) => {
				console.log('notification permission request result', res);
			})
		}
		return;
	}, []);

	if (loading) return null;

	return (
		<>
			<ToastContainer
				position="top-center"
				autoClose={3500}
				hideProgressBar={true}
				newestOnTop={true}
				closeOnClick
				rtl={false}
				pauseOnFocusLoss
				pauseOnHover
				theme="light"
			/>

			{user && user.isAnonymous !== true && <FeatureAnnouncement newReleases={newReleases} />}

			<BrowserRouter history={history}>
				<Layout>
					<Router />
				</Layout>
			</BrowserRouter>
		</>
	);
}

export default App;
